/**
 * ============================================================================
 * Y ŞERİDİ — OTOMATİK ŞİFRELİ BULUT YEDEĞİ (27.09.2026)
 * ============================================================================
 *
 * TETİKLEYEN OLAY: 27.09'da canlıda "Clear site data" tarayıcıdaki defteri
 * sildi. `.pfai` vardı ama elle alınan bir dosyaydı ve alınmamıştı. Kullanıcı
 * kararı: defter her değişiklikten sonra ŞİFRELİ olarak kullanıcının seçtiği
 * buluta KENDİLİĞİNDEN yazılsın.
 *
 * ÜÇ KARAR (kullanıcı, 27.09.2026):
 *  1. **Sunucu hiçbir şey tutmaz.** Merkez yedeğin nerede olduğunu bilmez;
 *     kimlik bilgileri, anahtar ve dosya içeriği sunucuya hiç uğramaz. Site
 *     verisi silinirse kullanıcı aynı buluta yeniden bağlanır, uygulama
 *     yedeği o bulutun UYGULAMAYA AYRILMIŞ klasöründe kendisi bulur.
 *     DEĞİŞTİ (kullanıcı kararı 16–17.10.2026): "Parafon sunucusu" hedefi
 *     seçilirse sunucu, açamadığı şifreli `.pfai` dosyalarını kasa koduyla
 *     tutar (`hedefler/parafonSunucu.ts`). Seçilmedikçe kural aynı.
 *  2. **Hedefler:** eşitlenen yerel klasör, Dropbox, Google Drive, OneDrive.
 *  3. **Anahtar cihazda, dışa çıkarılamaz.** Parola kurulumda bir kez girilir;
 *     ondan türetilen AES anahtarı `extractable: false` saklanır.
 *
 * DOSYALAR SIRADAN `.pfai`: parolayla her yerde açılır (Ayarlar → şifreli
 * yedeği geri yükle). Otomatik yedeğin ayrı bir biçimi YOK.
 */

import { M } from "../dil";
import type { PfaiKdf } from "../lib/pfai";

export type HedefTuru = "yerel-klasor" | "dropbox" | "google-drive" | "onedrive" | "parafon-sunucu";

export const HEDEF_ADI: Readonly<Record<HedefTuru, string>> = {
  "yerel-klasor": M.yedek.hedefAdi.yerelKlasor,
  dropbox: "Dropbox",
  "google-drive": "Google Drive",
  onedrive: "OneDrive",
  "parafon-sunucu": M.yedek.hedefAdi.parafonSunucu,
};

/** Buluttaki bir dosya — yalnızca listelemek ve seçmek için. */
export interface BulutDosyasi {
  readonly ad: string;
  /** ISO zaman damgası; hedef söylemiyorsa null. */
  readonly degisti: string | null;
  readonly boyut: number | null;
}

/**
 * "Bu oturumda sessizce yazabilir miyim" sorusunun cevabı.
 *
 * `etkilesim-gerekli` bir ARIZA değil, bir DURUM: tarayıcı güvenlik gereği
 * bazı izinleri yalnızca kullanıcının tıklamasıyla yeniler (yerel klasör izni,
 * Google'ın 1 saatlik erişim anahtarı, OneDrive'ın 24 saatlik yenileme
 * anahtarı). O durumda yedek BEKLER ve ekranda tek tıklık bir "yeniden bağlan"
 * düğmesi görünür — bekleyen yedek bir anormalliktir, gizlenmez.
 */
export type HedefHazirlik = "hazir" | "etkilesim-gerekli";

export interface BulutHedefi {
  readonly tur: HedefTuru;
  /** Kullanıcıya gösterilecek konum ("Dropbox › Uygulamalar › Parafon"). */
  readonly konum: string;
  hazirlik(): Promise<HedefHazirlik>;
  /**
   * Kullanıcının tıklamasıyla çağrılır (açılır pencere ya da izin istemi
   * yalnızca bir kullanıcı hareketinin içinden açılabilir).
   */
  yenidenBaglan(): Promise<void>;
  yaz(ad: string, metin: string): Promise<void>;
  listele(): Promise<readonly BulutDosyasi[]>;
  oku(ad: string): Promise<string>;
  sil(ad: string): Promise<void>;
  /**
   * Hedefin KALICI hâli (yenileme anahtarı, klasör tutamacı…). Hedef bir
   * anahtarı yeniledikçe değişir; yedekleyici her işten sonra bunu kasaya
   * geri yazar.
   */
  kayit(): HedefKaydi;
}

/**
 * Hedefin kasada saklanan hâli. IndexedDB yapısal kopyalama kullanıyor, yani
 * `FileSystemDirectoryHandle` da burada DOĞRUDAN saklanabiliyor.
 */
export type HedefKaydi =
  | { readonly tur: "yerel-klasor"; readonly klasor: FileSystemDirectoryHandle; readonly ad: string }
  | {
      readonly tur: "dropbox";
      readonly yenilemeAnahtari: string;
      readonly erisim: ErisimAnahtari | null;
    }
  | { readonly tur: "google-drive"; readonly erisim: ErisimAnahtari | null }
  | {
      readonly tur: "onedrive";
      readonly yenilemeAnahtari: string | null;
      /** Yenileme anahtarının SERT bitişi (SPA: ilk girişten 24 saat). */
      readonly yenilemeBitisi: string | null;
      readonly erisim: ErisimAnahtari | null;
    }
  | {
      readonly tur: "parafon-sunucu";
      /** Kanonik kasa kodu (26 karakter). Profilin şifreli kabında durur. */
      readonly kod: string;
    };

export interface ErisimAnahtari {
  readonly deger: string;
  /** ISO; bundan sonra kullanılmaz. */
  readonly bitis: string;
}

/** Otomatik yedeğin anahtarı: dışa çıkarılamaz `CryptoKey` + türetme başlığı. */
export interface YedekAnahtari {
  readonly key: CryptoKey;
  readonly kdf: PfaiKdf;
}

export interface SonYedek {
  readonly zaman: string;
  /** Yedeklenen defterin özeti (dışa aktarma zamanı hariç) — aynıysa yazılmaz. */
  readonly ozet: string;
  readonly dosya: string;
}

export interface SonHata {
  readonly zaman: string;
  readonly mesaj: string;
}

/**
 * Kasadaki tek kayıt — bu tarayıcının otomatik yedek kurulumu.
 *
 * SÜRÜM 1 (eski, ayrı yedek parolası): kasada paroladan türetilmiş
 * çıkarılamaz anahtar duruyordu — tarayıcıya erişen herkes onunla yedekleri
 * çözebilirdi. SÜRÜM 2 (kilitli defter, 06.10.2026): kasada ANAHTAR YOK;
 * yedek Worker'da defterin ana anahtarıyla şifrelenir (`.pfai` v2). Sürüm 1
 * kayıt kilitli defterde ilk turda sürüm 2'ye çevrilir, anahtar silinir.
 */
export type KasaKaydi = KasaKaydiV1 | KasaKaydiV2;

export interface KasaKaydiV1 {
  readonly surum: 1;
  readonly anahtar: YedekAnahtari;
  readonly hedef: HedefKaydi;
  readonly son: SonYedek | null;
  readonly hata: SonHata | null;
}

export interface KasaKaydiV2 {
  readonly surum: 2;
  readonly hedef: HedefKaydi;
  readonly son: SonYedek | null;
  readonly hata: SonHata | null;
  /** Bu cihazın eski biçimli (v1, tarihli adlı) bulut dosyaları silindi mi (D4). */
  readonly eskiTemizlendi: boolean;
  /**
   * Eşitleme ad göçü: bu cihazın tek parçalı önekli v2 dosyaları
   * (`parafon-<cihaz>-…`) — yalnızca BU DEFTERE ait olanlar — silindi mi.
   */
  readonly adGocu?: boolean;
}
